import {
  Box,
  Typography,
  Button,
  Grid,
  MenuItem,
  Select,
  FormControl,
  Divider,
} from "@mui/material";
import { forwardRef } from "react";
import { useImperativeHandle } from "react";
import { useEffect } from "react";
import { useState } from "react";

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
const SAP_API_ENDPOINT = process.env.REACT_APP_API_SAP;

function AcademicHistoryForm(props, ref) {
  const [coursesLevel, setCoursesLevel] = useState([]);
  const [schools, setSchools] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedLevelName, setSelectedLevelName] = useState("");
  const [selectedLevelObj, setSelectedLevelObj] = useState(null);
  const [selectedUniType, setSelectedUniType] = useState(null);
  const [selectedInstitution, setSelectedInstitution] = useState("");

  // Map labels to local image files
  const imageMap = {
    1: "SAPImages/academic/9th.png",
    2: "SAPImages/academic/10th.png",
    3: "SAPImages/academic/11th.png",
    4: "SAPImages/academic/12th.png",
    5: "SAPImages/academic/degree.png",
    6: "SAPImages/academic/masters.png",
    Public: "SAPImages/academic/public.png",
    Private: "SAPImages/academic/private.png",
  };

  const institutionType = [
    { id: 1, name: "Private" },
    { id: 2, name: "Public" },
  ];

  useEffect(() => {
    // Fetch menu items using the fetch function
    fetch(`${API_ENDPOINT}course-levels`)
      .then((response) => response.json())
      .then((data) => {
        setCoursesLevel(data?.data);
      })
      .catch((error) => {
        console.error("Error fetching courses:", error);
      });
  }, []);

  useEffect(() => {
    // Fetch menu items using the fetch function
    fetch(`${SAP_API_ENDPOINT}get-school-board`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        category: selectedUniType,
        course_level: selectedLevelObj?.gradlynk_id,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        setSchools(data?.data);
      })
      .catch((error) => {
        console.error("Error fetching narrow fields:", error);
      });
  }, [selectedUniType]);

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (!selectedLevel) {
        alert("Please select your highest academic level");
        return false;
      }
      if (!selectedUniType) {
        alert("Please select your University Type / School Board");
        return false;
      }
      if (!selectedInstitution) {
        alert("Please select your School / University");
        return false;
      }
      return true;
    },

    getData: () => ({
      highest_level_id: selectedLevel,
      school_university_type: selectedUniType,
      school_university_id: selectedInstitution,
    }),
  }));

  return (
    <Box
      className="scrollbar-hide"
      sx={{
        //  Add Scrollbar
        maxHeight: "400px",
        overflowY: "auto",

        /* Optional: smooth scrollbar look */
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#D9D9D9",
          borderRadius: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#F5F5F5",
        },
      }}
    >
      {/* Question 1: Academic Level */}
      <Typography
        sx={{
          fontFamily: "Inter",
          fontWeight: 500,
          fontStyle: "normal",
          fontSize: "medium",
          lineHeight: "100%",
          letterSpacing: "0px",
          verticalAlign: "middle",
          mb: 2,
          color: "#111111",
        }}
      >
        What’s Your Highest Academic Level?
      </Typography>

      <Grid container spacing={2}>
        {coursesLevel.map((level) => (
          <Grid item xs={12} sm={6} md={4} key={level.id}>
            <Button
              fullWidth
              onClick={() => {
                setSelectedLevel(level.gradlynk_id);
                setSelectedLevelName(level.course_level);
                setSelectedLevelObj(level);
                setSelectedUniType(null);
                setSelectedInstitution("");
              }}
              sx={{
                borderRadius: "8px",
                border: "1px solid #572575",
                boxShadow: "0px 0px 10px 0px #0000001A",

                // FIXED → use gradlynk_id everywhere
                color: selectedLevel === level.gradlynk_id ? "#572575" : "#000",
                backgroundColor:
                  selectedLevel === level.gradlynk_id ? "#F9EEFF" : "#fff",
                borderColor:
                  selectedLevel === level.gradlynk_id ? "#9F47D4" : "#E0E0E0",

                "&:hover": {
                  backgroundColor:
                    selectedLevel === level.gradlynk_id ? "#F9EEFF" : "#f5f5f5",
                },

                textTransform: "none",
                fontFamily: "Inter",
                fontSize: "16px",
                fontWeight: selectedLevel === level.gradlynk_id ? 600 : 400,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                p: 2,
              }}
            >
              <img
                src={imageMap[level.id]}
                alt={level.course_level}
                style={{ width: "40px", height: "40px", objectFit: "contain" }}
              />
              {level.course_level}
            </Button>
          </Grid>
        ))}
      </Grid>

      {selectedLevel && (
        <Box mt={4}>
          {/* University Type */}
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 500,
              fontStyle: "normal",
              fontSize: "medium",
              lineHeight: "100%",
              letterSpacing: "0px",
              verticalAlign: "middle",
              mb: 2,
              color: "#111111",
            }}
          >
            {selectedLevelName === "Bachelors" ||
            selectedLevelName === "Masters"
              ? "What's Your University Type?"
              : "What's Your School Board?"}
          </Typography>

          <Grid container spacing={2}>
            {institutionType.map((type) => (
              <Grid item xs={12} sm={6} md={4} key={type.id}>
                <Button
                  fullWidth
                  onClick={() => {
                    setSelectedUniType(type.id);
                    setSelectedInstitution("");
                  }}
                  sx={{
                    borderRadius: "8px",
                    boxShadow: "0px 0px 10px 0px #0000001A",
                    border: "1px solid #572575",
                    color: selectedUniType === type.id ? "#572575" : "#000",
                    backgroundColor:
                      selectedUniType === type.id ? "#F9EEFF" : "#fff",
                    borderColor:
                      selectedUniType === type.id ? "#9F47D4" : "#E0E0E0",
                    "&:hover": {
                      backgroundColor:
                        selectedUniType === type.id ? "#F9EEFF" : "#f5f5f5",
                    },
                    textTransform: "none",
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: selectedUniType === type.id ? 600 : 400,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    p: 2,
                  }}
                >
                  <img
                    src={imageMap[type.name]}
                    alt={type.name}
                    style={{
                      width: "40px",
                      height: "40px",
                      objectFit: "contain",
                    }}
                  />
                  {type.name}
                </Button>
              </Grid>
            ))}
          </Grid>

          <FormControl fullWidth sx={{ mt: 4 }}>
            <Select
              value={selectedInstitution}
              onChange={(e) => setSelectedInstitution(e.target.value)}
              displayEmpty
              sx={{
                height: "52px", // same height
                borderRadius: "12px", // soft rounded corners
                backgroundColor: "#FFFFFF",
                boxShadow: "0px 1px 4px rgba(0,0,0,0.15)", // subtle outer shadow
                ".MuiOutlinedInput-notchedOutline": {
                  border: "none", // remove border
                },
                fontFamily: "Inter",
                fontSize: "16px",
                color: "#111111",
                pl: 2,
              }}
              IconComponent={(props) => (
                <svg
                  {...props}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  style={{ marginRight: "12px" }}
                >
                  <path d="M7 10l5 5 5-5H7z" />
                </svg>
              )}
            >
              <MenuItem value="">
                <em style={{ fontStyle: "normal", color: "#6B6B6B" }}>
                  Select School
                </em>
              </MenuItem>

              {schools?.map((school) => (
                <MenuItem key={school?.id} value={school?.id}>
                  {school?.university_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
    </Box>
  );
}

export default forwardRef(AcademicHistoryForm);
